<script setup lang="ts">
import ParagraphTemplate from '@/views/projects-template/ParagraphTemplate.vue'

const sections = [
  {
    title: 'Welcome to the SPIN tool!',
    content: `
      <p style="margin-bottom: 10px">
        In this app, you can run spatial analyses to help you and your clients visualize and
        understand how resources and critical infrastructure are being distributed across the
        selected population, identifying communities at risk of losing access due to
        disastrous events.
      </p>
      <p style="margin-bottom: 10px"><strong>About SPIN (and why it was born)</strong></p>
      <p style="margin-bottom: 10px">
        Social vulnerability, as a multidimensional variable, tries to capture the
        socio-economic and demographic characteristics of a place. It helps in to
        communicating why communities may experience disasters differently, despite being
        affected by the same magnitude or severity of the event. Social vulnerability can
        result from a multitude (and combination) of factors, such as income, gender, age,
        education, social safety nets, and many more.<br /><br />
        By assessing the extent to which to critical facilities - such as drinking water
        points, food production locations, markets, schools and hospitals - are accessible to
        different communities, a component of social vulnerability is revealed - the spatial
        inequality part.<br /><br />
        The perception of impactful and reliable communication of spatial inequality is
        reliant on the ability to visualize distributions within a community. We found that
        without the tangible outputs of spatial maps, it was difficult to show our clients
        where infrastructure was failing the communities, and how these issue might be
        addressed. This is how the idea of Spatial Inequality Tool (SPIN) was born. This
        initiative sought to automate the time-consuming process of running a multitude of
        network analyses in GIS, simplify the traditional complex environment set-up and
        reducing the cost, and turning it into a user-friendly tool.
      </p>
      <p style="margin-bottom: 10px">Benefits of the tool:</p>
      <ul style="padding-left: 20px; margin-bottom: 10px">
        <li>
          Given its streamlined design, many more colleagues can use spatial inequality
          analyses to gain insights into infrastructure accessibility from a community
          perspective.
        </li>
        <li>
          The speed of the tool enables its use during business development, delivers
          opportunities for project time savings (or supports justification for additional
          time for more in-depth analyses), and supports a greater volume and multi-scenario
          analyses.
        </li>
        <li>
          Compared to the manual approach, this tool saves a significant amount of time.
        </li>
        <li>
          It provides a platform to showcase and spotlight our ‘Social Value and Equality’
          goals.
        </li>
      </ul>
    `,
    image: '/header_lazy.jpg',
    imageAlt: 'SPIN Tool Image'
  },
  {
    title: 'Spatial approach to Social Vulnerabilities',
    content: `
      <p style="margin-bottom: 10px">
        Social Vulnerability of a population can be determined by multiple factors, one of
        which is uneven distribution of resources, services or critical infrastructure (e.g.
        sanitation, healthcare, education, public services etc.) - known as Spatial
        Inequality. Spatial inequality can exacerbate existing socio-economic disparities,
        especially when faced within disasters which makes mobility more challenging, and
        limits access to resources and services. Communities who suffer from infrastructural
        exclusion and lack sufficient resources to overcome shortages are most sensitive to
        severe consequences of climate change. <br /><br />
        With a focus on road infrastructure, disasters such as floods, earthquakes,
        landslides or wildfires pose significant risks to road operability. This affects,
        affecting those who heavily rely on road infrastructure to access resources in varying
        degrees. In this case of a disaster, any extensive road downtime of roads may
        potentially isolate people, and restrict access e people from reaching to hospitals or
        food sources, leading to long-lasting consequences. To inform targeted and effective
        risk mitigations, mitigate the risks, it is essential to identify the most vulnerable
        communities and their mobility patterns. This can be delivered, for instance by
        undertaking spatial analyses with the use of advanced digital tools.<br /><br />
        The biggest advantages of the spatial approach to social vulnerabilities are the
        ability to understand how critical infrastructure is being distributed, and to
        recognize and identify communities that are at risk of being cut off from critical
        infrastructure during disasters. By identifying areas facing infrastructural poverty,
        our clients can better allocate resources and plan interventions to ensure the safety
        and well-being of affected populations.
      </p>
    `
  },
  {
    title: 'How does SPIN work?',
    content: `
      <p style="margin-bottom: 10px">
        In essence, the tool provides insights into how people move via road infrastructure
        from point A (origin) to point B (resource to or other points of interest) and what
        the impact is if some routes become inaccessible. The primary objective of the Tool is
        to:
      </p>
      <ul style="padding-left: 20px; margin-bottom: 10px">
        <li>
          <strong
            >Identify communities which have poor access to critical infrastructure on daily
            basis.</strong
          >
        </li>
        <li>
          <strong
            >Identify areas that get impacted by road disruptions during disastrous
            events.</strong
          >
        </li>
        <li>
          <strong>Understand who are the users of critical roads.</strong>
        </li>
        <li>
          <strong>Protect critical road infrastructure and facilities.</strong>
        </li>
      </ul>
    `
  },
  {
    title: 'Who can use the tool?',
    content: `
      <p style="margin-bottom: 10px">
        Everyone! <br /><br />
        There are two ways of using the SPIN tool - either as an active user (this mode allows
        a user to create projects, run simulations and assign viewers to the project) or as a
        viewer (this mode allows a user to view assigned content without modifying it or
        running analyses). Each mode requires different access rights. To run simulations, a
        user is required to log in using an ESRI account (the most basic license, costing 12
        Euros per month at time of writing), not view results of a simulation, no license is
        needed – in this case simple viewer’s Haskoning credentials are sufficient.<br /><br />
        The rationale behind the dual functionality is to promote the tool amongst our
        colleagues and clients and be able to showcase results of the spatial analyses to
        clients and stakeholders to purchase an ESRI license. In this way, we try to make
        social assessments even more inclusive.<br /><br />
      </p>
    `
  }
]
</script>

<template>
  <ParagraphTemplate title="About SPIN" :sections="sections" />
</template>

<style scoped></style>
